import React from "react";
import { ExportDatasetPhase, CodeTab } from "./types";
import { IconFrame } from "../../IconFrame";

// Thresholds chosen to match user perception: "slow" appears before most users
// would abandon, "stalled" appears after the p95 export time for large datasets.
export const SLOW_THRESHOLD_S = 20;
export const STALLED_THRESHOLD_S = 60;
export const MESSAGE_ROTATE_INTERVAL_S = 3;

export const exportDatasetMessages = {
    rotating: [
        "Zipping images and annotations...",
        "Building your dataset archive...",
        "Almost there, wrapping things up..."
    ],
    slow: "Larger datasets take longer. Hang tight.",
    stalled: "This is taking longer than usual. You can keep waiting or close and try again later."
} as const;

export const codeTabs: { key: CodeTab; label: string; icon: string }[] = [
    { key: "jupyter", label: "Jupyter", icon: "far fa-book-open" },
    { key: "terminal", label: "Terminal", icon: "fas fa-terminal" },
    { key: "raw", label: "Raw URL", icon: "fas fa-link" }
];

export const phaseFooterConfig: Record<
    ExportDatasetPhase,
    { submitText: string; submitHandler: string; cancelText?: string; showFooter: boolean }
> = {
    select: {
        submitText: "Export",
        submitHandler: "submit",
        cancelText: "Cancel",
        showFooter: false
    },
    exporting: { submitText: "Export", submitHandler: "submit", showFooter: false },
    error: {
        submitText: "Try Again",
        submitHandler: "retry",
        cancelText: "Cancel",
        showFooter: true
    }
};

export const splitConfig = [
    {
        key: "train" as const,
        label: "Train",
        color: "#6D28D9",
        textColor: "#6D28D9",
        icon: "fas fa-running"
    },
    {
        key: "valid" as const,
        label: "Valid",
        color: "#38BDF8",
        textColor: "#0284C7",
        icon: "fas fa-shield-check"
    },
    {
        key: "test" as const,
        label: "Test",
        color: "#EAB308",
        textColor: "#A16207",
        icon: "fas fa-vial"
    }
];

export const exportDatasetDeliveryItems = [
    {
        text: (
            <div className="flex flex-col gap-1 py-1">
                <div className="flex items-center gap-2">
                    <IconFrame icon="far fa-file-zipper" size="sm" color="gray" />
                    <span className="text-sm font-medium text-gray-800">ZIP file</span>
                </div>
                <p className="text-xs text-gray-500">
                    Download images, annotations, and class list as a single archive.
                </p>
            </div>
        ),
        value: "zip"
    },
    {
        text: (
            <div className="flex flex-col gap-1 py-1">
                <div className="flex items-center gap-2">
                    <IconFrame icon="far fa-code" size="sm" color="gray" />
                    <span className="text-sm font-medium text-gray-800">Code snippet</span>
                </div>
                <p className="text-xs text-gray-500">
                    Download directly into Colab, Jupyter, or your training environment.
                </p>
            </div>
        ),
        value: "code"
    }
];

/** Maps dataset type descriptions (as stored in Firestore) to Font Awesome icon classes. */
export const typeIcons: Record<string, string> = {
    "Object Detection": "far fa-draw-square",
    "Instance Segmentation": "far fa-circle-nodes",
    "Semantic Segmentation": "far fa-puzzle-piece",
    "Classification": "far fa-tag",
    "Multi-Label Classification": "far fa-tag",
    "Single-Label Classification": "far fa-tag",
    "Keypoint Detection": "far fa-diagram-project",
    "Multimodal": "far fa-layer-group"
};
